import React, { useEffect, useMemo, useState } from "react";
import {
  Empty,
  Spin,
  Table,
  Tag,
  Modal,
  Descriptions,
  Button,
  Divider,
  Typography,
  Segmented,
  Input,
  DatePicker,
} from "antd";
import {
  HomeOutlined,
  PhoneOutlined,
  UserOutlined,
  CalendarOutlined,
  TeamOutlined,
  ReloadOutlined,
  ApartmentOutlined,
  PlusOutlined,
  EyeOutlined,
  HistoryOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import RoomServiceApi from "../services/RoomServiceApi";
import BillService from "../services/Billservice";
import BillPanelNoContext from "../components/biils/BillPanelNoContext";
import GuestOrdersQueue from "../components/GuestOrders/GuestOrdersQueue";
import { useBilling } from "../hooks/useBilling";
import { findOrCreateRoomTable } from "../utils/roomTableLink";
import { notify } from "../components/Customs/NotifyProvider";
import {
  canViewModule,
  canCreateModule,
  canUpdateModule,
  canDeleteModule,
} from "../utils/permissionUtils";
import "../components/biils/TableViewNoContext.css";
import "./RoomService.css";

const { Text } = Typography;
const { RangePicker } = DatePicker;

const MODULE_KEY = "room_service";

const STATUS_STYLES = {
  Reserved: { bg: "rgba(124,58,237,0.12)", color: "#7c3aed" },
  Confirmed: { bg: "rgba(25,185,107,0.12)", color: "#15803d" },
  "Checked-in": { bg: "rgba(61,140,255,0.12)", color: "#1d4ed8" },
  "Checked-out": { bg: "rgba(135,149,170,0.14)", color: "#475569" },
  Cancelled: { bg: "rgba(240,68,92,0.12)", color: "#dc2626" },
  "No Show": { bg: "rgba(255,157,61,0.14)", color: "#b45309" },
};

const normalizeStatus = (value) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

  const statusMap = {
    reserved: "Reserved",
    confirmed: "Confirmed",
    checkedin: "Checked-in",
    occupied: "Checked-in",
    checkedout: "Checked-out",
    checkout: "Checked-out",
    exited: "Checked-out",
    cancelled: "Cancelled",
    canceled: "Cancelled",
    noshow: "No Show",
  };

  return statusMap[normalized] || "Confirmed";
};

// A booking's `rooms` field is a comma-separated list of room ids.
const parseRoomIds = (rawRooms) =>
  String(rawRooms || "")
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);

const RoomCard = ({
  booking,
  onNewBill,
  onViewBill,
  busy,
  canCreateBill,
  canOpenBills,
}) => {
  const statusStyle = STATUS_STYLES[booking.status] || STATUS_STYLES.Confirmed;

  return (
    <div className="rs-card">
      <div className="rs-card-head">
        <div className="rs-card-title-wrap">
          <div className="rs-card-title">{booking.roomLabel}</div>
          <div className="rs-card-meta">
            <UserOutlined /> {booking.guestName}
          </div>
        </div>

        <div
          className="rs-status-badge"
          style={{ background: statusStyle.bg, color: statusStyle.color }}
        >
          {booking.status}
        </div>
      </div>

      {booking.billInfo?.billCount > 0 && (
        <div className="tb-bill-pill">
          <span className="tb-bill-count">{booking.billInfo.itemCount}</span>
          <span>
            {booking.billInfo.billCount === 1 ? "Active Bill" : "Active Bills"} ·{" "}
            {booking.billInfo.itemCount}{" "}
            {booking.billInfo.itemCount === 1 ? "item" : "items"} · Not settled
          </span>
        </div>
      )}

      {booking.rooms.length > 0 && (
        <div className="rs-room-list">
          {booking.rooms.map((room, index) => (
            <div className="rs-room-chip" key={`${booking.id}-${room.id ?? index}`}>
              <ApartmentOutlined />
              <span>
                Room {room.roomNumber}
                {room.floor ? ` • Floor ${room.floor}` : ""}
                {room.typeName ? ` • ${room.typeName}` : ""}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="rs-card-body">
        <div className="rs-info-row">
          <PhoneOutlined />
          <span>{booking.phone}</span>
        </div>

        <div className="rs-info-row">
          <CalendarOutlined />
          <span>{booking.stay}</span>
        </div>

        <div className="rs-info-row">
          <TeamOutlined />
          <span>{booking.guests} Guest(s)</span>
        </div>
      </div>

      {canOpenBills && booking.status !== "Checked-out" && (
        <div className="tb-card-actions rs-card-actions">
          {canCreateBill && (
            <button
              className="tb-btn secondary"
              onClick={() => onNewBill(booking)}
              disabled={busy}
              type="button"
            >
              <PlusOutlined />
              New Bill
            </button>
          )}

          <button
            className="tb-btn primary"
            onClick={() => onViewBill(booking)}
            disabled={busy}
            type="button"
          >
            <EyeOutlined />
            View Bill
          </button>
        </div>
      )}
    </div>
  );
};

const formatBillDateTime = (value) =>
  value ? dayjs(value).format("DD MMM YYYY, hh:mm A") : "-";

const formatBillRoomLabel = (bill) => {
  const room = bill.room_number || bill.table_name;
  if (!room) return "-";

  const parts = [`Room ${room}`];
  if (bill.room_floor) parts.push(`Floor ${bill.room_floor}`);
  if (bill.room_type_name) parts.push(bill.room_type_name);

  return parts.join(" • ");
};

const RoomBillDetailModal = ({ bill, open, onClose }) => {
  if (!bill) return null;

  const items = Array.isArray(bill.items) ? bill.items : [];
  const totals = bill.totals || {};
  const discount = Number(bill.discount || 0);

  return (
    <Modal
      title="Bill Details"
      open={open}
      onCancel={onClose}
      width={760}
      footer={
        <div className="form-actions" style={{ marginTop: 0 }}>
          <Button onClick={onClose} className="secondary-btn">
            Close
          </Button>
        </div>
      }
    >
      <div style={{ marginBottom: 16 }}>
        <div className="permission-module-title">{bill.bill_no || bill.id}</div>
        <div className="permission-module-subtitle">
          View ordered items and payment summary
        </div>
      </div>

      <Descriptions column={2} size="middle" bordered>
        <Descriptions.Item label="Bill No">{bill.bill_no || "-"}</Descriptions.Item>
        <Descriptions.Item label="Room">{formatBillRoomLabel(bill)}</Descriptions.Item>
        <Descriptions.Item label="Guest">{bill.guest_name || "-"}</Descriptions.Item>
        <Descriptions.Item label="Phone">{bill.guest_phone || "-"}</Descriptions.Item>
        <Descriptions.Item label="Payment">
          <Tag color="green">{bill.payment_method || "N/A"}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Open Time">
          {formatBillDateTime(bill.created_at)}
        </Descriptions.Item>
        <Descriptions.Item label="Closed Time" span={2}>
          {formatBillDateTime(bill.closed_at)}
        </Descriptions.Item>
      </Descriptions>

      <Divider />

      <div className="table-card-header" style={{ marginBottom: 12 }}>
        <div>
          <h3>Ordered Items</h3>
          <p>Items included in this bill</p>
        </div>
      </div>

      <div className="common-table-wrap">
        <Table
          dataSource={items}
          rowKey={(r) => r.id || `${r.name}-${r.qty}`}
          pagination={false}
          size="middle"
          columns={[
            {
              title: "Item",
              dataIndex: "name",
              render: (_, r) => (
                <span>
                  <Text strong>{r.name || r.item_name || "-"}</Text>
                  {Number(r.is_parcel) === 1 && (
                    <Tag color="orange" style={{ marginLeft: 8 }}>
                      Parcel
                    </Tag>
                  )}
                </span>
              ),
            },
            { title: "Qty", dataIndex: "qty", width: 90, align: "center" },
            {
              title: "Price",
              width: 120,
              align: "right",
              render: (_, r) => `₹${Number(r.price || 0).toFixed(2)}`,
            },
            {
              title: "Total",
              width: 140,
              align: "right",
              render: (_, r) => (
                <Text strong>
                  ₹{(Number(r.price || 0) * Number(r.qty || 0)).toFixed(2)}
                </Text>
              ),
            },
          ]}
          locale={{
            emptyText: (
              <Empty description="No items found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ),
          }}
        />
      </div>

      <Divider />

      <div className="table-card-header" style={{ marginBottom: 12 }}>
        <div>
          <h3>Payment Summary</h3>
          <p>Breakdown from bill totals</p>
        </div>
      </div>

      <Descriptions bordered column={3} size="middle" style={{ marginTop: 12 }}>
        <Descriptions.Item label="Food Subtotal">
          ₹{Number(totals.subtotal || 0).toFixed(2)}
        </Descriptions.Item>

        <Descriptions.Item label="Food CGST">
          ₹{Number(totals.cgst || 0).toFixed(2)}
        </Descriptions.Item>

        <Descriptions.Item label="Food SGST">
          ₹{Number(totals.sgst || 0).toFixed(2)}
        </Descriptions.Item>

        {Number(totals.parcel_charge || 0) > 0 && (
          <Descriptions.Item label="Parcel Charge">
            ₹{Number(totals.parcel_charge).toFixed(2)}
          </Descriptions.Item>
        )}

        {Number(totals.ac_charge || 0) > 0 && (
          <Descriptions.Item label="AC Charge">
            ₹{Number(totals.ac_charge).toFixed(2)}
          </Descriptions.Item>
        )}

        {discount > 0 && (
          <Descriptions.Item label="Discount">₹{discount.toFixed(2)}</Descriptions.Item>
        )}

        <Descriptions.Item label="Tax">
          ₹{Number(totals.tax || 0).toFixed(2)}
        </Descriptions.Item>

        <Descriptions.Item label="Grand Total" span={2}>
          <Text strong style={{ fontSize: 16 }}>
            ₹{Math.round(Number(totals.total || bill.final_total || 0)).toLocaleString("en-IN")}
          </Text>
        </Descriptions.Item>
      </Descriptions>
    </Modal>
  );
};

const RoomService = () => {
  const [view, setView] = useState("rooms"); // "rooms" | "history" | "orders"
  const [tab, setTab] = useState("checkedin");
  const [pendingOrderCount, setPendingOrderCount] = useState(0);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [busyBookingId, setBusyBookingId] = useState(null);
  const [syncedFromCache, setSyncedFromCache] = useState(false);
  const [cacheSyncedAt, setCacheSyncedAt] = useState(null);

  const [historyBills, setHistoryBills] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [viewingBill, setViewingBill] = useState(null);
  const [historyPeriod, setHistoryPeriod] = useState("today");
  const [historyDateRange, setHistoryDateRange] = useState(null);
  const [historySearch, setHistorySearch] = useState("");

  // Room Service's own settled-bill history — completely separate from the
  // restaurant's Bill History page. Same /bills/closed endpoint, but with
  // source: "room_service" instead of the default (which excludes it).
  const loadHistory = async (filters = {}) => {
    try {
      setHistoryLoading(true);
      setHistoryError("");

      const data = await BillService.getClosedBills({
        source: "room_service",
        ...filters,
      });
      setHistoryBills(Array.isArray(data) ? data : []);
    } catch (err) {
      setHistoryError(err?.message || "Unable to load room service history.");
      setHistoryBills([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleHistoryRefresh = () => {
    if (historyPeriod === "today") loadHistory({ date: "today" });
    else if (historyPeriod === "yesterday") loadHistory({ date: "yesterday" });
    else if (historyPeriod === "all") loadHistory();
    else if (historyPeriod === "range" && historyDateRange?.length === 2) {
      loadHistory({
        from: historyDateRange[0].format("YYYY-MM-DD"),
        to: historyDateRange[1].format("YYYY-MM-DD"),
      });
    }
  };

  useEffect(() => {
    if (view === "history") {
      handleHistoryRefresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, historyPeriod, historyDateRange]);

  const filteredHistoryBills = useMemo(() => {
    if (!historySearch) return historyBills;

    const q = historySearch.toLowerCase();
    return historyBills.filter((b) => {
      const room = formatBillRoomLabel(b);
      return (
        String(b.bill_no || "").toLowerCase().includes(q) ||
        room.toLowerCase().includes(q) ||
        String(b.guest_name || "").toLowerCase().includes(q) ||
        String(b.payment_method || "").toLowerCase().includes(q)
      );
    });
  }, [historyBills, historySearch]);

  const {
    menuItems,
    categories,
    generalSettings,
    billModalState,
    setBillModalState,
    getTableById,
    registerTable,
    getActiveBillsForTable,
    getBillTotal,
    refreshBillsForTable,
    createBill,
    deleteBill,
    updateBillItems,
    updateBillMeta,
    closeBill,
    splitBill,
    mergeBillsInTable,
  } = useBilling({ moduleKey: MODULE_KEY });

  const canCreateBill = canCreateModule(MODULE_KEY);
  const canUpdateBill = canUpdateModule(MODULE_KEY);
  const canDeleteBill = canDeleteModule(MODULE_KEY);
  const canOpenBills = canCreateBill || canUpdateBill;

  const loadBookings = async () => {
    try {
      setLoading(true);
      setError("");

      const res = await RoomServiceApi.getRoomBookings();

      const rawList = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res)
          ? res
          : [];

      // The handed-off StayMaster token can span every property that guest
      // has access to, but this restaurant should only ever show bookings
      // for the property(ies) actually linked to it. No linked properties
      // (or the lookup failing) means nothing is shown rather than leaking
      // every property's rooms.
      const linkedPropertyIds = await BillService.getLinkedPropertyIds().catch(() => []);
      const allowedPropertyIds = new Set(
        (Array.isArray(linkedPropertyIds) ? linkedPropertyIds : []).map(String)
      );

      const list = allowedPropertyIds.size
        ? rawList.filter((booking) => allowedPropertyIds.has(String(booking.property_id)))
        : [];

      // Resolve every room id referenced across all bookings to its real
      // room_number / floor / type — bookings only carry raw room ids.
      const uniqueRoomIds = [
        ...new Set(list.flatMap((booking) => parseRoomIds(booking.rooms))),
      ];

      const [roomTypesRes, roomSettled] = await Promise.all([
        RoomServiceApi.getRoomTypes().catch(() => null),
        Promise.allSettled(
          uniqueRoomIds.map((id) => RoomServiceApi.getRoomById(id))
        ),
      ]);

      const roomTypeList = Array.isArray(roomTypesRes?.data)
        ? roomTypesRes.data
        : Array.isArray(roomTypesRes)
          ? roomTypesRes
          : [];

      const roomTypeMap = roomTypeList.reduce((acc, type) => {
        acc[String(type.id)] =
          type.room_name || type.room_type_name || type.name || `Type #${type.id}`;
        return acc;
      }, {});

      const roomMap = {};
      uniqueRoomIds.forEach((id, index) => {
        const settled = roomSettled[index];
        if (settled.status !== "fulfilled") return;

        const room = settled.value?.data || settled.value;
        if (!room) return;

        roomMap[id] = {
          id,
          roomNumber: room.room_number || room.room_no || `#${id}`,
          floor: room.floor || "",
          typeName:
            room.room_type_name ||
            roomTypeMap[String(room.room_type)] ||
            "",
        };
      });

      // Any room that's already been billed at least once has a hidden
      // "RoomSvc-<roomId>" table row (see roomTableLink.js). Pull those in
      // bulk and check for still-open (unsettled) bills, so cards can show
      // an "Active Bill" badge without creating a table for every room that
      // has never been billed.
      const allTables = await BillService.getAllRaw("tables").catch(() => []);
      const roomServiceTables = (Array.isArray(allTables) ? allTables : []).filter(
        (t) => typeof t.name === "string" && t.name.startsWith("RoomSvc-")
      );

      const openBillsByTable = await Promise.all(
        roomServiceTables.map((t) => BillService.getBillsByTable(t.id).catch(() => []))
      );

      const billInfoByRoomId = {};
      roomServiceTables.forEach((t, idx) => {
        const roomId = t.name.replace("RoomSvc-", "");
        const bills = openBillsByTable[idx] || [];
        const itemCount = bills.reduce(
          (sum, b) => sum + (Array.isArray(b.items) ? b.items.length : 0),
          0
        );

        billInfoByRoomId[roomId] = { billCount: bills.length, itemCount };
      });

      const formatted = list.map((booking, index) => {
        const roomIds = parseRoomIds(booking.rooms);
        const rooms = roomIds
          .map((id) => roomMap[id])
          .filter(Boolean);

        const primaryRoomId = rooms[0]?.id;
        const billInfo = primaryRoomId ? billInfoByRoomId[primaryRoomId] : null;

        return {
          id: booking.id ?? index,
          rooms,
          billInfo,
          roomLabel:
            rooms.length > 0
              ? `Room - ${rooms.map((r) => r.roomNumber).join(", ")}`
              : booking.id
                ? `Booking BK-${booking.id}`
                : `Booking #${index}`,
          guestName: booking.guest_name || booking.name || "Guest",
          phone: booking.phone || booking.mobile || booking.contact || "-",
          guests: booking.no_of_guest || booking.guests || "-",
          stay:
            booking.checkin_in_date && booking.checkin_out_date
              ? `${dayjs(booking.checkin_in_date).format("DD MMM")} - ${dayjs(
                  booking.checkin_out_date
                ).format("DD MMM YYYY")}`
              : "-",
          status: normalizeStatus(booking.booking_status || booking.status),
        };
      });

      setBookings(formatted);
      setSyncedFromCache(false);

      // Fire-and-forget: let other terminals (with no live StayMaster
      // session of their own) fall back to this snapshot.
      BillService.syncRoomServiceCache(formatted).catch(() => {});
    } catch (err) {
      console.error("ROOM SERVICE FETCH ERROR:", err);

      const cache = await BillService.getRoomServiceCache().catch(() => null);

      if (cache?.bookings?.length) {
        setBookings(cache.bookings);
        setSyncedFromCache(true);
        setCacheSyncedAt(cache.syncedAt);
        setError("");
      } else {
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Unable to load room data from the booking system."
        );
        setBookings([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBookings();
  }, []);

  const refreshPendingOrderCount = async () => {
    try {
      const pending = await BillService.getGuestOrders("pending");
      setPendingOrderCount(Array.isArray(pending) ? pending.length : 0);
    } catch {
      // Best-effort badge — a failed count check shouldn't disrupt the page.
    }
  };

  useEffect(() => {
    refreshPendingOrderCount();
  }, []);

  const checkedInCount = useMemo(
    () => bookings.filter((b) => b.status === "Checked-in").length,
    [bookings]
  );

  const filteredBookings = useMemo(() => {
    if (tab === "checkedin") {
      return bookings.filter((b) => b.status === "Checked-in");
    }
    return bookings;
  }, [tab, bookings]);

  // Keyed by table_id, so whichever room's panel is currently open, ANY bill
  // created for it — including via the bill panel's own internal "+ New
  // Bill"/"Create First Bill" button, which only ever calls onAddBill(tableId)
  // with no other context — still gets tagged with the right room/guest info.
  const [roomFieldsByTableId, setRoomFieldsByTableId] = useState({});

  // Every hotel room gets a hidden backing "table" row (see roomTableLink.js)
  // so the existing bill system (Add Food/Parcel Items, split, settle...)
  // can be reused as-is for room service orders. Bills created here are
  // tagged source: "room_service" so they never show up in the regular
  // restaurant Bill History / reports.
  const resolveRoomTable = async (booking) => {
    const primaryRoom = booking.rooms[0];
    const table = await findOrCreateRoomTable(primaryRoom.id);

    registerTable({
      id: table.id,
      name: booking.roomLabel,
      capacity: table.capacity,
      zone: primaryRoom.typeName || (primaryRoom.floor ? `Floor ${primaryRoom.floor}` : ""),
      status: table.status,
      ac_type: table.ac_type,
    });

    const roomServiceFields = {
      source: "room_service",
      room_number: primaryRoom.roomNumber,
      room_floor: primaryRoom.floor || null,
      room_type_name: primaryRoom.typeName || null,
      guest_name: booking.guestName,
      guest_phone: booking.phone,
      staymaster_room_id: primaryRoom.id,
      staymaster_booking_id: booking.id,
    };

    setRoomFieldsByTableId((prev) => ({ ...prev, [table.id]: roomServiceFields }));

    return { table, primaryRoom, roomServiceFields };
  };

  // Passed to BillPanelNoContext as onAddBill — always attaches the current
  // room's metadata, whichever UI button triggered the creation.
  const createRoomBill = (tableId, extraFields = {}) =>
    createBill(tableId, { ...(roomFieldsByTableId[tableId] || {}), ...extraFields });

  const handleNewBill = async (booking) => {
    if (!booking.rooms.length) {
      notify.warning(
        "Room Not Resolved",
        "Could not identify a room for this booking yet — try refreshing."
      );
      return;
    }

    setBusyBookingId(booking.id);

    try {
      // Use the just-resolved fields directly rather than createRoomBill —
      // the setRoomFieldsByTableId call above hasn't been applied to state
      // yet at this point (state updates aren't synchronous), so reading
      // roomFieldsByTableId here would still see the old, empty value.
      const { table, roomServiceFields } = await resolveRoomTable(booking);
      const newBill = await createBill(table.id, roomServiceFields);

      setBillModalState({ open: true, tableId: table.id, billId: newBill?.id ?? null });
    } catch (err) {
      notify.error(
        "Unable to Create Bill",
        err?.response?.data?.message || err?.message || "Something went wrong."
      );
    } finally {
      setBusyBookingId(null);
    }
  };

  const handleViewBill = async (booking) => {
    if (!booking.rooms.length) {
      notify.warning(
        "Room Not Resolved",
        "Could not identify a room for this booking yet — try refreshing."
      );
      return;
    }

    setBusyBookingId(booking.id);

    try {
      const { table } = await resolveRoomTable(booking);
      const bills = await refreshBillsForTable(table.id);

      if (!bills.length) {
        notify.info("No Bills Yet", "This room has no bill yet — click New Bill to start one.");
        return;
      }

      setBillModalState({ open: true, tableId: table.id, billId: null });
    } catch (err) {
      notify.error(
        "Unable to Open Bill",
        err?.response?.data?.message || err?.message || "Something went wrong."
      );
    } finally {
      setBusyBookingId(null);
    }
  };

  const selectedTable = billModalState?.tableId
    ? getTableById(billModalState.tableId)
    : null;

  const selectedBills = billModalState?.tableId
    ? getActiveBillsForTable(billModalState.tableId)
    : [];

  const handleClosePanel = () => {
    setBillModalState({ open: false, tableId: null, billId: null });
    // Refresh so each card's "Active Bill" badge reflects any items added
    // or bills settled while the panel was open.
    loadBookings();
  };

  if (!canViewModule(MODULE_KEY)) {
    return (
      <div className="page-shell user-master-page">
        <div className="page-empty">
          <Empty description="No permission to view Room Service" />
        </div>
      </div>
    );
  }

  return (
    <div className="page-shell user-master-page">
      <div className="user-page-header">
        <div className="user-page-header-left">
          <div className="user-page-icon">
            <HomeOutlined />
          </div>

          <div>
            <h2 className="user-page-title">Room Service</h2>
            <p className="user-page-subtitle">
              Live check-in room status pulled from the hotel booking
              application.
            </p>
          </div>
        </div>

        <div className="user-page-header-actions">
          <button
            type="button"
            className={`secondary-btn rs-refresh-btn ${view === "rooms" ? "rs-view-active" : ""}`}
            onClick={() => setView("rooms")}
          >
            <HomeOutlined /> Rooms
          </button>

          <button
            type="button"
            className={`secondary-btn rs-refresh-btn ${view === "history" ? "rs-view-active" : ""}`}
            onClick={() => setView("history")}
          >
            <HistoryOutlined /> Room Service History
          </button>

          <button
            type="button"
            className={`secondary-btn rs-refresh-btn ${view === "orders" ? "rs-view-active" : ""}`}
            onClick={() => setView("orders")}
          >
            <HomeOutlined /> Guest Orders
            {pendingOrderCount > 0 && (
              <span className="rs-pending-badge">{pendingOrderCount}</span>
            )}
          </button>

          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
            <button
              className="secondary-btn rs-refresh-btn"
              onClick={
                view === "history"
                  ? handleHistoryRefresh
                  : view === "orders"
                  ? refreshPendingOrderCount
                  : loadBookings
              }
              type="button"
            >
              <ReloadOutlined spin={view === "history" ? historyLoading : loading} /> Refresh
            </button>
            <span style={{ fontSize: 12, color: "#9ca3af" }}>Refresh to get live data from StayMaster</span>
          </div>
        </div>
      </div>

      {view === "orders" ? (
        <div className="page-card table-card">
          <div className="table-card-header">
            <div>
              <h3>Guest Orders</h3>
              <p>Food orders placed from the StayMaster guest app — confirm to add them to that room's bill.</p>
            </div>
          </div>

          <GuestOrdersQueue
            onOrderActioned={refreshPendingOrderCount}
            generalSettings={generalSettings}
            menuItems={menuItems}
            categories={categories}
          />
        </div>
      ) : view === "history" ? (
        <>
          <div className="page-card filter-card">
            <div className="filter-card-header">
              <h3>Filters</h3>
              <span>{filteredHistoryBills.length} bills found</span>
            </div>

            <div className="filters-inline-row">
              <div className="filter-item user-filter-item">
                <label className="field-label">Period</label>
                <Segmented
                  value={historyPeriod}
                  onChange={setHistoryPeriod}
                  options={[
                    { label: "Today", value: "today" },
                    { label: "Yesterday", value: "yesterday" },
                    { label: "Date Range", value: "range" },
                    { label: "All", value: "all" },
                  ]}
                  block
                />
              </div>

              {historyPeriod === "range" && (
                <div className="filter-item user-filter-item">
                  <label className="field-label">Date Range</label>
                  <RangePicker
                    onChange={(vals) => setHistoryDateRange(vals)}
                    className="page-search w-full"
                  />
                </div>
              )}

              <div className="filter-item module-search-item">
                <label className="field-label">Search</label>
                <Input
                  placeholder="Search bill no, room, guest, payment..."
                  prefix={<SearchOutlined />}
                  value={historySearch}
                  onChange={(e) => setHistorySearch(e.target.value)}
                  allowClear
                  className="page-search"
                />
              </div>

              <div className="filter-item action-btn-item">
                <label className="field-label">&nbsp;</label>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleHistoryRefresh}
                  className="secondary-btn"
                >
                  Refresh
                </Button>
              </div>
            </div>
          </div>

          <div className="page-card table-card">
            <div className="table-card-header">
              <div>
                <h3>Room Service History</h3>
                <p>Settled room service bills — kept separate from the restaurant's Bill History.</p>
              </div>
            </div>

            {historyLoading ? (
              <div className="page-empty">
                <Spin size="large" />
              </div>
            ) : historyError ? (
              <div className="page-empty">
                <Empty description={historyError} />
              </div>
            ) : filteredHistoryBills.length === 0 ? (
              <div className="page-empty">
                <Empty description="No settled room service bills for this filter" />
              </div>
            ) : (
              <div className="common-table-wrap">
                <Table
                  rowKey="id"
                  pagination={{ pageSize: 10, showSizeChanger: false }}
                  dataSource={filteredHistoryBills}
                  columns={[
                    { title: "Bill No", dataIndex: "bill_no" },
                    {
                      title: "Room",
                      dataIndex: "room_number",
                      render: (_, record) => formatBillRoomLabel(record),
                    },
                    { title: "Guest", dataIndex: "guest_name", render: (v) => v || "-" },
                    { title: "Phone", dataIndex: "guest_phone", render: (v) => v || "-" },
                    {
                      title: "Items",
                      dataIndex: "items",
                      render: (items) => (Array.isArray(items) ? items.length : 0),
                    },
                    {
                      title: "Payment",
                      dataIndex: "payment_method",
                      render: (v) => <Tag color="green">{v || "-"}</Tag>,
                    },
                    {
                      title: "Open Time",
                      dataIndex: "created_at",
                      render: (v) => (v ? dayjs(v).format("DD MMM YYYY, hh:mm A") : "-"),
                    },
                    {
                      title: "Close Time",
                      dataIndex: "closed_at",
                      render: (v) => (v ? dayjs(v).format("DD MMM YYYY, hh:mm A") : "-"),
                    },
                    {
                      title: "Action",
                      key: "action",
                      width: 80,
                      align: "center",
                      render: (_, record) => (
                        <Button
                          size="small"
                          shape="circle"
                          icon={<EyeOutlined />}
                          className="table-icon-btn edit-btn"
                          onClick={() => setViewingBill(record)}
                        />
                      ),
                    },
                  ]}
                />
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="page-card table-card">
          <div className="table-card-header">
            <div>
              <h3>Room Overview</h3>
              <p>Filter by check-in status and view guest details quickly.</p>
            </div>
          </div>

          <div className="tb-toolbar">
            <div className="tb-filter-group">
              <button
                type="button"
                className={`tb-chip ${tab === "checkedin" ? "active" : ""}`}
                onClick={() => setTab("checkedin")}
              >
                Checked-in Rooms ({checkedInCount})
              </button>

              <button
                type="button"
                className={`tb-chip ${tab === "all" ? "active" : ""}`}
                onClick={() => setTab("all")}
              >
                All Bookings ({bookings.length})
              </button>
            </div>
          </div>

          {loading ? (
            <div className="page-empty">
              <Spin size="large" />
            </div>
          ) : error ? (
            <div className="page-empty">
              <Empty description={error} />
            </div>
          ) : filteredBookings.length === 0 ? (
            <div className="page-empty">
              <Empty description="No rooms found" />
            </div>
          ) : (
            <div className="tb-grid-wrap">
              <div className="rs-grid">
                {filteredBookings.map((booking) => (
                  <RoomCard
                    key={booking.id}
                    booking={booking}
                    onNewBill={handleNewBill}
                    onViewBill={handleViewBill}
                    busy={busyBookingId === booking.id}
                    canCreateBill={canCreateBill}
                    canOpenBills={canOpenBills}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {billModalState?.open && selectedTable && (
        <BillPanelNoContext
          table={selectedTable}
          bills={selectedBills}
          initialBillId={billModalState.billId}
          onClose={handleClosePanel}
          menuItems={menuItems}
          categories={categories}
          onAddBill={canCreateBill ? createRoomBill : undefined}
          onDeleteBill={canDeleteBill ? deleteBill : undefined}
          onMergeBills={canUpdateBill ? mergeBillsInTable : undefined}
          onSplitBill={canUpdateBill ? splitBill : undefined}
          onUpdateBillItems={canUpdateBill ? updateBillItems : undefined}
          onUpdateBillMeta={canUpdateBill ? updateBillMeta : undefined}
          onCloseBill={canUpdateBill ? closeBill : undefined}
          getBillTotal={getBillTotal}
          refreshBillsForTable={refreshBillsForTable}
          canCreateBill={canCreateBill}
          canUpdateBill={canUpdateBill}
          canDeleteBill={canDeleteBill}
          generalSettings={generalSettings}
          hideCapacity
        />
      )}

      <RoomBillDetailModal
        bill={viewingBill}
        open={!!viewingBill}
        onClose={() => setViewingBill(null)}
      />
    </div>
  );
};

export default RoomService;
